<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay">
      <div class="modal-content dashboard-task-create-modal" @click.stop>
        <div class="modal-header">
          <h3 v-if="showProjectSelection">{{ s__('Dashboard|Choose project') }}</h3>
          <h3 v-else>{{ s__('TaskForm|New Task') }}</h3>
          <button class="modal-close" aria-label="Close" @click="closeModal">✕</button>
        </div>

        <div class="modal-body" :class="{ 'is-form-step': !showProjectSelection }">
          <div v-if="showProjectSelection" class="dashboard-project-select">
            <div v-if="projects.length === 0" class="dashboard-empty-state">
              <h4>{{ s__('Dashboard|No active projects yet') }}</h4>
              <p>
                {{
                  s__('Dashboard|Create a project first, then you will be able to create tasks.')
                }}
              </p>
              <button type="button" class="btn-sm btn-primary" @click="openProjectCreateFlow">
                {{ s__('Dashboard|Create Project') }}
              </button>
            </div>
            <p v-else class="dashboard-project-select-hint">
              {{ s__('Dashboard|Select a project to create a task in.') }}
            </p>
            <ul v-if="projects.length > 0" class="dashboard-project-select-list">
              <li v-for="project in projects" :key="project.id">
                <button
                  type="button"
                  class="dashboard-project-select-item"
                  @click="selectProject(project.id)"
                >
                  {{ project.title }}
                </button>
              </li>
            </ul>
          </div>

          <div v-else>
            <NewTaskPage
              :project-id="String(effectiveProjectId)"
              :chat-id="chatId"
              :from-id="fromId"
              :embedded="true"
              :embedded-project-title="selectedProject?.title"
              :can-go-back-to-project-selection="canGoBackToProjectSelection"
              @task-created="handleTaskCreated"
              @back-to-project-selection="goBackToProjectSelection"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { s__ } from '@/locale'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import NewTaskPage from '../projects/tasks/New.vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  projects: { type: Array, default: () => [] },
  initialProjectId: { type: [String, Number], default: null },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
})

const emit = defineEmits(['close', 'created', 'open-project-create'])

const step = ref('project')
const selectedProjectId = ref(null)
const forceProjectSelection = ref(false)

const effectiveProjectId = computed(() => {
  if (selectedProjectId.value) return selectedProjectId.value
  if (props.initialProjectId) return props.initialProjectId
  if (props.projects.length === 1) return props.projects[0].id
  return null
})

const selectedProject = computed(() =>
  props.projects.find(project => String(project.id) === String(effectiveProjectId.value))
)

const showProjectSelection = computed(
  () => forceProjectSelection.value || !effectiveProjectId.value
)

const canGoBackToProjectSelection = computed(
  () => !props.initialProjectId && props.projects.length > 1 && !showProjectSelection.value
)

useBodyScrollLock(computed(() => props.visible))

watch(
  [() => props.initialProjectId, () => props.projects],
  () => {
    // Pre-seed modal state while hidden to avoid first-frame content jump on open.
    if (props.visible) return
    initializeModalState()
  },
  { immediate: true, deep: true }
)

watch(
  () => props.visible,
  isVisible => {
    if (!isVisible) return
    // Always initialize on open so first painted frame matches incoming props.
    initializeModalState()
  }
)

watch(
  () => props.projects,
  projects => {
    if (!props.visible || !projects.length) return
    if (selectedProject.value) return
    initializeModalState()
  },
  { deep: true }
)

function initializeModalState() {
  forceProjectSelection.value = false
  const initial = props.initialProjectId
  if (initial) {
    selectProject(initial)
    return
  }

  if (props.projects.length === 1) {
    selectProject(props.projects[0].id)
    return
  }

  selectedProjectId.value = null
  step.value = 'project'
}

function selectProject(projectId) {
  forceProjectSelection.value = false
  selectedProjectId.value = projectId
  step.value = 'form'
}

function goBackToProjectSelection() {
  forceProjectSelection.value = true
  selectedProjectId.value = null
  step.value = 'project'
}

function closeModal() {
  emit('close')
}

function openProjectCreateFlow() {
  emit('close')
  emit('open-project-create')
}

function handleTaskCreated(payload) {
  emit('created', payload)
}
</script>

<style scoped>
.dashboard-task-create-modal {
  max-width: 860px;
  height: 90vh;
  max-height: 90vh;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
}

.dashboard-task-create-modal .modal-body {
  flex: 1;
  overflow: auto;
  overscroll-behavior: contain;
}

.modal-body.is-form-step {
  min-height: 0;
}

.dashboard-project-select-hint {
  margin-top: 0;
  color: #666;
}

.dashboard-empty-state h4 {
  margin: 0 0 8px;
}

.dashboard-empty-state p {
  margin: 0 0 12px;
  color: #666;
}

.dashboard-project-select-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dashboard-project-select-item {
  width: 100%;
  text-align: left;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
  color: #333;
}

.dashboard-project-select-item:hover {
  background: #f9fafb;
  border-color: #2aabee;
}
</style>
